import React, { useState, useMemo } from 'react';
import { Search, Users, Loader, AlertCircle } from 'lucide-react';
import { useDocketFilers } from '../../hooks/useDocketFilers';

export interface SelectedStakeholder {
  filer_name: string;
  submission_ids: string[];
}

interface StakeholderSelectorProps {
  docketNumber: string;
  selectedStakeholders: SelectedStakeholder[];
  onChange: (stakeholders: SelectedStakeholder[]) => void;
}

export const StakeholderSelector: React.FC<StakeholderSelectorProps> = ({
  docketNumber,
  selectedStakeholders,
  onChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { filers, isLoading, error } = useDocketFilers(docketNumber || null);

  // Build a set of selected filer names for O(1) lookup
  const selectedNames = useMemo(
    () => new Set(selectedStakeholders.map((s) => s.filer_name)),
    [selectedStakeholders]
  );

  // Filter filers by search query
  const filteredFilers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return filers;
    return filers.filter((f) => f.filer_name.toLowerCase().includes(q));
  }, [filers, searchQuery]);

  const handleToggle = (filerName: string, submissionIds: string[]) => {
    if (selectedNames.has(filerName)) {
      onChange(selectedStakeholders.filter((s) => s.filer_name !== filerName));
    } else {
      onChange([...selectedStakeholders, { filer_name: filerName, submission_ids: submissionIds }]);
    }
  };

  const handleSelectAll = () => {
    // Merge all filtered filers into the selection (keep any already-selected ones)
    const merged = new Map<string, SelectedStakeholder>(
      selectedStakeholders.map((s) => [s.filer_name, s])
    );
    filteredFilers.forEach((f) => {
      merged.set(f.filer_name, {
        filer_name: f.filer_name,
        submission_ids: f.submission_ids,
      });
    });
    onChange(Array.from(merged.values()));
  };

  const handleClearAll = () => {
    // Remove any filers that appear in the current filtered list
    const filteredNames = new Set(filteredFilers.map((f) => f.filer_name));
    onChange(selectedStakeholders.filter((s) => !filteredNames.has(s.filer_name)));
  };

  const selectedCount = selectedStakeholders.length;

  return (
    <div className="mb-4">
      {/* Label row */}
      <div className="flex items-center justify-between mb-1">
        <div className="block text-sm font-medium text-gray-700">
          Relevant Stakeholders
        </div>
        {selectedCount > 0 && (
          <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            {selectedCount} {selectedCount === 1 ? 'stakeholder' : 'stakeholders'} selected
          </span>
        )}
      </div>

      {/* Helper text */}
      <p className="text-xs text-gray-500 mb-3">
        Select filers to receive deeper analysis in your summary.
      </p>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <Loader className="w-4 h-4 animate-spin text-gray-400 shrink-0" />
          <span className="text-sm text-gray-500">Loading filers...</span>
        </div>
      )}

      {/* Error state */}
      {!isLoading && error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Empty state (loaded, no error, no filers) */}
      {!isLoading && !error && filers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-6 bg-gray-50 border border-gray-200 rounded-lg text-center">
          <Users className="w-8 h-8 text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No filers found for this docket.</p>
        </div>
      )}

      {/* Filer list */}
      {!isLoading && !error && filers.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search filers..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              />
            </div>
          </div>

          {/* Select / Clear all row */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-100">
            <span className="text-xs text-gray-500">
              {filteredFilers.length} {filteredFilers.length === 1 ? 'filer' : 'filers'}
              {searchQuery ? ' matching' : ''}
            </span>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-emerald-600 hover:text-emerald-800 underline"
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-gray-500 hover:text-gray-700 underline"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Scrollable list */}
          <div className="max-h-52 overflow-y-auto divide-y divide-gray-100">
            {filteredFilers.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-sm text-gray-400">No filers match your search.</p>
              </div>
            ) : (
              filteredFilers.map((filer) => {
                const isSelected = selectedNames.has(filer.filer_name);
                return (
                  <label
                    key={filer.filer_name}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-emerald-50 hover:bg-emerald-100/70'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(filer.filer_name, filer.submission_ids)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 shrink-0"
                    />
                    <span className="flex-1 text-sm text-gray-900 font-medium leading-snug">
                      {filer.filer_name}
                    </span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {filer.filing_count} {filer.filing_count === 1 ? 'filing' : 'filings'}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
